/* eslint-disable no-nested-ternary */
import { FC, useState } from 'react'
import { Button, Radio, Table, Tag, Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
	BroadcastContact,
	BroadcastContactLink,
	BroadcastContactsStatus,
} from 'types'
import getFullName, { LikeContactUser } from 'utils/getFullName'
import useSingleBroadcast from 'hooks/useSingleBroadcast'
import toPrecisionAmount from 'utils/toPrecisionAmount'
import useBroadcastContacts from 'hooks/useBroadcastContacts'
import getMobileNumber from 'utils/getMobileNumber'

import { useAppDispatch } from 'hooks/reduxHooks'
import {
	updateBroadcastContactPageAction,
	updateBroadcastContactProcessAction,
} from 'slices/broadcastContact'
import Panel from 'component/UI/Panel'
import { RiExternalLinkFill } from 'react-icons/ri'
import { InfoCircleOutlined } from '@ant-design/icons'
import styles from './styles.module.scss'

const getStatusColor = (status: BroadcastContactsStatus) => {
	switch (status) {
		case 'FAILED':
		case 'DELETED':
			return 'red'
		case 'READ':
		case 'SENT':
			return 'green'
		case 'DELIVERED':
			return '#17a2b8'
		case 'ENQUEUED':
			return 'yellow'
		default:
			return undefined
	}
}

const BroadcastContacts: FC = () => {
	const { broadcast } = useSingleBroadcast()
	const currencySelected = broadcast?.client?.currency_selected ?? 'INR'

	const dispatch = useAppDispatch()

	const {
		loading,
		currentPage,
		records,
		totalCount,
		// totalPages,
		size,
		process,
		// page,
	} = useBroadcastContacts()

	const [isLinkModalVisible, setIsLinkModalVisible] = useState(false)
	const [links, setLinks] = useState<BroadcastContactLink[]>([])

	const openLinksDrawer = (contactLinks: BroadcastContactLink[]) => {
		setIsLinkModalVisible(true)
		setLinks(contactLinks)
	}

	const closeLinksDrawer = () => {
		setIsLinkModalVisible(false)
		setLinks([])
	}

	const columns: ColumnsType<BroadcastContact> = [
		{
			title: 'Name',
			dataIndex: ['contact', 'firstName'],
			width: 200,
			fixed: 'left',
			render: (firstName: string, row) =>
				getFullName(row.contact as LikeContactUser),
		},
		{
			title: 'Number',
			dataIndex: ['contact', 'mobile'],
			render: (mobile: any) => getMobileNumber(mobile),
		},
		{
			title: 'Status',
			dataIndex: ['conversation', 'status'],
			render: (status: BroadcastContactsStatus, record) => (
				<>
					<Tag
						color={getStatusColor(
							status || record.broadcast_status?.toLocaleUpperCase() || 'PENDING'
						)}
					>
						{status || record.broadcast_status?.toLocaleUpperCase() || 'PENDING'}
					</Tag>
					{status === 'FAILED' && (
						<Tooltip title={record.conversation?.remarks}>
							<InfoCircleOutlined />
						</Tooltip>
					)}
				</>
			),
		},
		{
			title: `Cost (${broadcast?.currency || currencySelected})`,
			dataIndex: ['message_cost', 'total'],
			render: (chargeInInr: number | null) =>
				chargeInInr ? toPrecisionAmount(chargeInInr) : 0,
		},
		{
			title: 'Billed',
			dataIndex: ['is_billed'],
			render: (isBilled: boolean, record) =>
				process ? (
					<Tag
						color={
							isBilled
								? 'green'
								: record.conversation?.status !== 'FAILED' &&
								  record.broadcast_status !== 'failed'
								? 'orange'
								: 'red'
						}
					>
						{isBilled
							? 'Yes'
							: record.conversation?.status !== 'FAILED' &&
							  record.broadcast_status !== 'failed'
							? 'In Session'
							: 'No'}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: 'Link clicks',
			dataIndex: 'links',
			align: 'center',
			render: (contactLinks: BroadcastContactLink[]) =>
				contactLinks && contactLinks.length > 0 ? (
					<Button
						icon={<RiExternalLinkFill />}
						type='ghost'
						onClick={
							contactLinks.length > 0 ? () => openLinksDrawer(contactLinks) : undefined
						}
					>
						{contactLinks.length > 1
							? `+${contactLinks.length.toString()}`
							: undefined}
					</Button>
				) : (
					'--'
				),
		},
	]

	const handleStatusChange = (value: boolean) => {
		dispatch(updateBroadcastContactProcessAction(value))
	}

	return (
		<div>
			<Radio.Group
				className={styles.radio}
				value={process ? 'processed' : 'in-process'}
				onChange={e => handleStatusChange(e.target.value === 'processed')}
				options={[
					{
						label: 'In Process',
						value: 'in-process',
					},
					{
						label: 'Processed',
						value: 'processed',
					},
				]}
				optionType='button'
			/>
			<Table
				className={styles.table}
				columns={columns}
				dataSource={records}
				loading={loading}
				pagination={{
					current: currentPage,
					total: totalCount,
					showSizeChanger: false,
					pageSize: size,
					onChange: page => {
						dispatch(updateBroadcastContactPageAction(page))
					},
				}}
			/>
			<Panel
				title='Link Clicks'
				icon={<RiExternalLinkFill />}
				setShow={setIsLinkModalVisible}
				show={isLinkModalVisible}
				destroyOnClose
				onClose={closeLinksDrawer}
			>
				<div>
					<Table
						dataSource={links}
						rowKey='_id'
						columns={[
							{
								title: 'Link',
								dataIndex: 'url',
								render: (url: string, record: BroadcastContactLink) => (
									<a href={record.longUrl} target='blank'>
										{url}
									</a>
								),
							},
							{ title: 'Clicks', dataIndex: ['insight', 'total'] },
						]}
						pagination={false}
					/>
				</div>
			</Panel>
		</div>
	)
}

export default BroadcastContacts
